import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Check, ChevronLeft, Loader2 } from "lucide-react";
import artieLogo from "@/assets/images/artie.png";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // Load Geist font
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      // Optional: navigate to forgot-password or keep them here to see the error
      // navigate("/forgot-password"); 
    }
  }, [token, navigate]);

  // Clear errors on input
  useEffect(() => {
    if (error) setError("");
  }, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      triggerShake();
      return;
    }

    if (password.length < 8) {
      alert("Password must be at least 8 characters.");
      return;
    }

    if (!token) {
        toast.error("Missing reset token");
        return;
    }

    setIsLoading(true);

    try {
      // Execute the real API call
      await apiClient.resetPassword(token, password);
      
      // Show success state (simulating a slight delay for smooth transition if API is too fast, 
      // or just to match the visual flow requested)
      setIsSuccess(true);
      toast.success("Password reset successful");
      
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Failed to reset password");
      setError("Failed to reset password");
      triggerShake();
    } finally {
      setIsLoading(false);
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  return (
    <div
      className="antialiased bg-white text-neutral-900 min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      style={{ fontFamily: "'Geist', sans-serif" }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        .input-error { border-color: #ef4444 !important; }
        .input-error:focus { --tw-ring-color: #ef4444 !important; border-color: #ef4444 !important; }
        .shake { animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
      `}</style>


      {/* Main Content Card */}
      <main className="w-full max-w-[400px] px-4 relative z-10">
        
        {/* Header Logo */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <Link to="/login" className="flex items-center gap-2 group transition-opacity hover:opacity-80">
            <img src={artieLogo} alt="Artie" className="w-10 h-10 rounded-xl shadow-lg shadow-neutral-900/10" />
          </Link>
        </div>

        {/* State: Reset Form */}
        {!isSuccess ? (
          <div className={`bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-neutral-100 p-8 sm:p-10 animate-scale-in ${shake ? "shake" : ""}`}>
            <div className="text-center mb-8">
              <h1 className="text-xl font-semibold tracking-tight text-neutral-900 mb-2">Reset password</h1>
              <p className="text-neutral-500 text-sm leading-relaxed">
                Please choose a new password for your account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* New Password */}
              <div className="space-y-1.5 text-left">
                <label htmlFor="new-password" className="block text-sm font-medium text-neutral-700">New password</label>
                <div className="relative group">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    id="new-password" 
                    required 
                    placeholder="Enter new password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`block w-full h-11 pl-3.5 pr-10 rounded-lg border bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm text-sm ${error ? "border-red-500" : "border-neutral-200"}`}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5 text-left">
                <label htmlFor="confirm-password" className="block text-sm font-medium text-neutral-700">Confirm password</label>
                <div className="relative group">
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    id="confirm-password" 
                    required 
                    placeholder="Repeat new password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`block w-full h-11 pl-3.5 pr-10 rounded-lg border bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm text-sm ${error ? "border-red-500" : "border-neutral-200"}`}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors focus:outline-none"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {error && <p className="text-xs text-red-500 font-medium mt-1 animate-fade-in">{error}</p>}
              </div>

              {/* Requirements hint */}
              <ul className="text-xs text-neutral-400 space-y-1 pl-1">
                <li className="flex items-center gap-1.5">
                  <Check className="text-neutral-300" width={12} height={12} />
                  Must be at least 8 characters
                </li>
              </ul>

              <div className="pt-2">
                <button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full flex items-center justify-center h-11 px-4 rounded-lg text-white font-medium bg-neutral-900 hover:bg-neutral-800 active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-900 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin h-4 w-4" />
                  ) : (
                    "Reset password"
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="inline-flex items-center text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors group">
                <ChevronLeft width={16} height={16} className="mr-1.5 transition-transform group-hover:-translate-x-1" />
                Back to login
              </Link>
            </div>
          </div>
        ) : (
          /* State: Success Message */
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-neutral-100 p-8 sm:p-10 animate-scale-in">
            <div className="flex flex-col items-center text-center">
              {/* Success Icon */}
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-6 ring-4 ring-emerald-50/50 animate-scale-in">
                <Check className="text-emerald-500" width={24} height={24} strokeWidth={2} />
              </div>
              
              <h2 className="text-xl font-semibold tracking-tight text-neutral-900 mb-3">Password reset</h2>
              
              <p className="text-neutral-500 text-sm leading-relaxed mb-8">
                Your password has been successfully updated. You can now log in with your new credentials.
              </p>

              <Link to="/login" className="w-full flex items-center justify-center h-11 px-4 rounded-lg bg-neutral-900 text-white font-medium hover:bg-neutral-800 active:scale-[0.98] transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-900 text-sm">
                Continue to login
              </Link>
            </div>
          </div>
        )}

      </main>

      {/* Footer Info */}
      <footer className="absolute bottom-6 text-center w-full p-4">
        <p className="text-xs text-neutral-400">
          © Artie Inc. Secure access.
        </p>
      </footer>
    </div>
  );
}